import qs from "qs";
import POSConfigModule from "@/store/modules/pos-config";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import request from "@/utils/request";
import { SharedCallbacks } from "@/utils/shared-resources";

/**
 * Push Synchronization for local booking
 * Postman: synchronize bookings
 * @param booking PSB created locally
 */
// eslint-disable-next-line
export async function pushSyncBooking(
  bookings: IProductSlotBooking[]
): Promise<any> {
  const params: { [posConfigId: string]: number | undefined } = {};

  for (let i = 0; i < bookings.length; i++) {
    const booking = bookings[i];
    if (booking.firstPosConfigId) {
      params.posConfigId = booking.firstPosConfigId;
      break;
    }
  }
  // in case none of the bookings contained a posconfigid
  // i.e when bookings were created before releasing BOO-1775
  if (!params.posConfigId) {
    const getPOSConfigId = SharedCallbacks.getPOSConfigId;
    if (getPOSConfigId) {
      const posConfigId = getPOSConfigId();
      params.posConfigId = posConfigId;
      bookings.forEach((booking) => {
        booking.firstPosConfigId = posConfigId;
      });
    }
  }

  const response = await request({
    method: "POST",
    url: `/group/${POSConfigModule.groupId}/adyen/v1/synchronize-bookings`,
    data: {
      channel: "Smeetz POS",
      bookings,
    },
    params,
  });

  return response.data;
}

/**
 * Push Synchronization for updated local booking
 * @param booking updated locally
 */
// eslint-disable-next-line
export async function putSynchedBooking(updatedBooking: any): Promise<any> {
  const params: { [posConfigId: string]: number | undefined } = {};
  const getPOSConfigId = SharedCallbacks.getPOSConfigId;
  if (getPOSConfigId) {
    params.posConfigId = getPOSConfigId();
    if (updatedBooking.booking) {
      updatedBooking.booking.firstPosConfigId = getPOSConfigId();
    }
  }

  const response = await request({
    method: "PUT",
    url: `/group/${POSConfigModule.groupId}/adyen/v1/synchronize-bookings`,
    data: {
      updatedBooking,
    },
    params,
  });

  return response.data;
}

/**
 * Print booking
 * Postman: `POST_booking_print`
 */
export async function postBookingPosPrint(
  // eslint-disable-next-line
  data: any
): Promise<number> {
  const { bookingId, ...params } = data;
  const response = await request({
    url: `/booking/${bookingId}/print`,
    method: "post",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    data: qs.stringify(params),
  });
  return response.status;
}

/**
 * Email booking
 */
export async function postBookingEmail(data: any): Promise<number> {
  const { bookingId, email, first_name, last_name } = data;
  const params = {
    email,
    first_name,
    last_name,
  };
  const response = await request({
    url: `/booking/${bookingId}/pos/email`,
    method: "post",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    data: qs.stringify(params),
  });
  return response.status;
}

/**
 * Download single ticket
 */
export async function getDownloadTicket(
  pssId: number,
  bookingId: number
): Promise<string | null> {
  const response = await request({
    url: `/booking/${bookingId}/ticket/url?pssId=${pssId}`,
    method: "get",
  });
  if (!response.data.url) return null;
  return response.data.url;
}

/**
 * Generates full booking tickets
 */
export async function postDownloadBookingTickets(
  bookingId: number
): Promise<number> {
  const response = await request({
    url: `/group/${POSConfigModule.groupId}/posGenerateFiles`,
    method: "post",
    data: {
      bookingId,
    },
  });
  return response.status;
}

/**
 * Download full booking tickets
 */
export async function getDownloadBookingTickets(
  bookingId: number
): Promise<string | null> {
  const response = await request({
    url: `/booking/${bookingId}/ticket/url`,
    method: "get",
  });
  if (!response.data.url) return null;
  return response.data.url;
}

/**
 * Generate invoice
 */
export async function generateBookingInvoice(
  bookingId: number
): Promise<number> {
  const response = await request({
    url: `/booking/${bookingId}/invoice`,
    method: "post",
  });
  return response.status;
}

/**
 * Send invoice email
 * @param bookingId Booking id
 * @param customerOnly if set to 1, email is sent to the customer only
 */
export async function sendBookingInvoiceEmail(
  bookingId: number,
  customerOnly?: number
): Promise<number> {
  const param = customerOnly === 1 ? customerOnly : 0;
  const response = await request({
    url: `/booking/${bookingId}/invoice/send-email?email-customer-only=${param}`,
    method: "post",
  });
  return response.status;
}

/**
 * Download booking invoice
 */
export async function downloadBookingInvoice(
  bookingId: number
): Promise<string | null> {
  const response = await request({
    url: `/booking/${bookingId}/invoice/link`,
    method: "get",
  });
  console.log("here: ", response.data);
  if (!response.data.data.url) return null;
  return response.data.data.url;
}
